import { useEffect, useState } from 'react';
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { ScrollArea } from '@radix-ui/react-scroll-area';
import { getCurrentScope } from '@sentry/react';
import {
  Bot,
  ClipboardList,
  Heart,
  LayoutDashboard,
  LifeBuoy,
  Menu,
  PackageSearch,
  ScanSearch,
  Users,
} from 'lucide-react';

import Logo from '@/assets/icons/logo.svg?react';
import { CreditsCard } from '@/components/credits-card';
import { Nav } from '@/components/nav';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { UserMenu } from '@/components/user-menu';
import { ROUTES } from '@/constants/routes';
import { useUserContext } from '@/context/user';
import { useUser } from '@/features/auth';
import { PaymentProvider, PlanInterval, SubscriptionStatus } from '@/lib/api';
import { retainUIClient } from '@/retain-client';
import { isDevelopment, isStaging } from '@/utils/environment';

// TODO: make it collapsible
export const DefaultLayout = () => {
  const { user, setUser } = useUserContext();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  // NOTE: update local user data
  const { data: userData } = useUser();

  // NOTE: update user data, cause additional rerender
  useEffect(() => {
    if (userData) {
      setUser((user) => ({ ...user, ...userData }));
    }
  }, [userData, setUser]);

  // NOTE: sentry integration
  useEffect(() => {
    const scope = getCurrentScope();

    if (user) {
      scope.setUser({
        email: user.email,
        name: user.name,
        paymentProvider: user.payment_provider,
        customerId: user.customerId,
        paddle_customer_id: user.paddle_customer_id,
        paymentCustomerId: user.customerId || user.paddle_customer_id,
        subscriptionStatus: user.subscriptionStatus,
        plan: user.plan?.name,
        subscriptionExpiresAt: user.subscriptionExpiresAt,
      });
    }
  }, [user]);

  // NOTE: churnkey integration
  useEffect(() => {
    if (
      user?.payment_provider === PaymentProvider.STRIPE &&
      userData &&
      userData?.customerId &&
      window.churnkey?.check
    ) {
      const churnkeyConfig = {
        customerId: userData.customerId,
        authHash: userData.churnkeyHash,
        appId: 'gc9wahf9b',
        mode: isDevelopment || isStaging ? 'test' : 'live',
        provider: 'stripe',
      };

      window.churnkey.check('pause', {
        ...churnkeyConfig,
        softWall: false, // set to true to allow exiting wall
        forceCheck: false, // recommended to leave this to false to avoid redundant checks
      });

      window.churnkey.check('failed-payment', {
        ...churnkeyConfig,
        softWall: false, // set to true to allow exiting wall
        forceCheck: false, // recommended to leave this to false to avoid redundant checks
        gracePeriodDays: 1, // allow a grace period in which failed payment wall is shown but can be exited (soft wall)
        ignoreInvoicesWithoutAttempt: false, // set to true to ignore invoices without a failed charge
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // NOTE: initialize Paddle for payment recovery notifications
  useEffect(() => {
    if (
      user?.payment_provider === PaymentProvider.PADDLE &&
      user?.paddle_customer_id
    ) {
      retainUIClient(user.paddle_customer_id)
        .then(() => {
          console.log(
            'Paddle Retain initialized for payment recovery notifications'
          );
        })
        .catch((error) => {
          console.error('Failed to initialize Paddle Retain:', error);
        });
    }
  }, [user?.payment_provider, user?.paddle_customer_id, user?.email]);

  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [pathname]);

  if (!user) return <Navigate to={ROUTES.SIGN_IN} replace={true} />;

  const isPaymentCanceled =
    !user.subscriptionStatus ||
    user.subscriptionStatus === SubscriptionStatus.CANCELED;
  const isSubscriptionActive =
    new Date(user.subscriptionExpiresAt ?? '') > new Date();
  const isUserSubscriptionValid =
    user.subscriptionStatus && (!isPaymentCanceled || isSubscriptionActive);

  // NOTE: redirect to subscription page if user has no subscription
  if (
    !isUserSubscriptionValid &&
    pathname !== ROUTES.SUBSCRIPTION &&
    pathname !== ROUTES.SUBSCRIPTION_SUCCESS
  ) {
    return <Navigate to={ROUTES.SUBSCRIPTION} replace={true} />;
  }

  const hasYearlyPlan = user.plan?.interval === PlanInterval.YEAR;
  const isAiChatLocked = !hasYearlyPlan;

  const handleLockedFeatureClick = () => {
    setShowUpgradeDialog(true);
  };

  return (
    <div className="grid gap-6 p-4 lg:grid-cols-[minmax(200px,_220px)_1fr_1fr_1fr_1fr] lg:p-6">
      <aside className="sticky top-6 hidden h-[calc(100dvh-48px)] flex-col justify-between lg:col-span-1 lg:flex">
        <div>
          <Link to="/" className="mb-6 flex items-center gap-2">
            <Logo className="size-8" />
            <span className="text-lg font-semibold">Supplier HQ</span>
          </Link>
          <Nav
            disabled={!isUserSubscriptionValid}
            links={[
              {
                title: 'Dashboard',
                icon: PackageSearch,
                subLinks: [
                  {
                    title: 'Suppliers',
                    to: ROUTES.SUPPLIERS,
                    label: '10,000+',
                  },
                  {
                    title: 'Distributors',
                    to: ROUTES.DISTRIBUTORS,
                    label: '300+',
                  },
                ],
              },
              {
                title: 'Product Explorer',
                to: ROUTES.PRODUCT_CATEGORIES,
                icon: LayoutDashboard,
              },
              {
                title: 'Favorites',
                icon: Heart,
                subLinks: [
                  {
                    title: 'Suppliers',
                    to: ROUTES.FAVORITE_SUPPLIERS,
                  },
                  {
                    title: 'Distributors',
                    to: ROUTES.FAVORITE_DISTRIBUTORS,
                  },
                ],
              },
              {
                title: 'AI Chat',
                to: ROUTES.AI_CHAT,
                icon: Bot,
                locked: isAiChatLocked,
                requiresYearlyPlan: true,
                onLockedClick: handleLockedFeatureClick,
              },
              {
                title: 'CRM',
                to: ROUTES.FAVORITE_CRM,
                icon: ClipboardList,
              },
              {
                title: 'Search Demand',
                to: ROUTES.SEARCH_DEMAND,
                icon: ScanSearch,
              },
              // {
              //   title: 'Weekly Products',
              //   to: ROUTES.WEEKLY_PRODUCTS,
              //   icon: CalendarFold,
              // },
              {
                title: 'Outreach Templates',
                to: 'https://drive.google.com/drive/folders/1aCbGwsRTxqqmzYO5vLUq9k6Qa1oC25Om?usp=sharing',
                icon: Users,
                blank: true,
              },
              {
                title: 'Help Desk',
                to: 'https://help.highticket.io',
                icon: LifeBuoy,
                blank: true,
              },
              // {
              //   title: 'Concierge Service',
              //   to: '/',
              //   icon: ConciergeBell,
              // },
            ]}
          />
        </div>
        <div>
          <CreditsCard className="mb-2" disabled={!isUserSubscriptionValid} />
          <UserMenu />
        </div>
      </aside>
      <header className="flex justify-between lg:hidden">
        <Link to="/" className="flex items-center gap-2">
          <Logo className="size-8" />
          <span className="text-lg font-semibold">Supplier HQ</span>
        </Link>
        <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="size-10">
              <Menu className="size-5 text-primary" />
              <span className="sr-only">Toggle navigation menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent className="bg-card pt-12">
            <ScrollArea className="flex h-[calc(100dvh-72px)] flex-col justify-between">
              <Nav
                disabled={!isUserSubscriptionValid}
                links={[
                  {
                    title: 'Dashboard',
                    icon: PackageSearch,
                    subLinks: [
                      {
                        title: 'Suppliers',
                        to: ROUTES.SUPPLIERS,
                        label: '10,000+',
                      },
                      {
                        title: 'Distributors',
                        to: ROUTES.DISTRIBUTORS,
                        label: '300+',
                      },
                    ],
                  },
                  {
                    title: 'Product Explorer',
                    to: ROUTES.PRODUCT_CATEGORIES,
                    icon: LayoutDashboard,
                  },
                  {
                    title: 'Favorites',
                    icon: Heart,
                    subLinks: [
                      {
                        title: 'Suppliers',
                        to: ROUTES.FAVORITE_SUPPLIERS,
                      },
                      {
                        title: 'Distributors',
                        to: ROUTES.FAVORITE_DISTRIBUTORS,
                      },
                    ],
                  },
                  {
                    title: 'AI Chat',
                    to: ROUTES.AI_CHAT,
                    icon: Bot,
                    locked: isAiChatLocked,
                    requiresYearlyPlan: true,
                    onLockedClick: handleLockedFeatureClick,
                  },
                  {
                    title: 'CRM',
                    to: ROUTES.FAVORITE_CRM,
                    icon: ClipboardList,
                  },
                  {
                    title: 'Search Demand',
                    to: ROUTES.SEARCH_DEMAND,
                    icon: ScanSearch,
                  },
                  // {
                  //   title: 'Weekly Products',
                  //   to: ROUTES.WEEKLY_PRODUCTS,
                  //   icon: CalendarFold,
                  // },
                  {
                    title: 'Outreach Templates',
                    to: 'https://drive.google.com/drive/folders/1aCbGwsRTxqqmzYO5vLUq9k6Qa1oC25Om?usp=sharing',
                    icon: Users,
                    blank: true,
                  },
                  {
                    title: 'Help Desk',
                    to: 'https://help.supplierhq.io/en/',
                    icon: LifeBuoy,
                    blank: true,
                  },
                  // {
                  //   title: 'Concierge Service',
                  //   to: '/',
                  //   icon: ConciergeBell,
                  // },
                ]}
              />
              <div>
                <CreditsCard
                  className="mb-2"
                  disabled={!isUserSubscriptionValid}
                />
                <UserMenu />
              </div>
            </ScrollArea>
          </SheetContent>
        </Sheet>
      </header>
      <main className="overflow-y-auto lg:col-span-4">
        <Outlet />
      </main>
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="gap-6 sm:max-w-[400px]">
          <DialogHeader className="items-center space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Bot className="h-10 w-10 text-primary" />
            </div>
            <DialogTitle className="text-center">
              AI Chat is a Yearly Plan Feature
            </DialogTitle>
            <DialogDescription className="text-center">
              Upgrade to a yearly subscription to unlock AI-powered supplier
              discovery, intelligent product recommendations, and conversational
              search capabilities.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Button
              className="w-full"
              onClick={() => {
                navigate(`${ROUTES.SUBSCRIPTION}?interval=year`);
                setShowUpgradeDialog(false);
              }}
            >
              View Yearly Plans
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowUpgradeDialog(false)}
            >
              Maybe Later
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
